"use client";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Pusher from "pusher-js";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { SpeedDial } from "primereact/speeddial";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import { Chip } from "primereact/chip";
import { get, groupBy, isEmpty, isNull } from "lodash";
import { DashboardTitle } from "components/DashboardTitle";
import Stages from "./Stages";
import ScansBoard from "./ScansBoard";
import ScansTable from "./ScansTable";
import ScansBarChart from "./ScansBarChart";
import CustomiseLayout from "./CustomiseLayout";
import { URLS, METRIC_TYPES } from "utils/constants";
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";
import { useParams } from "react-router";
import exportModule from "./ExportModule";
import PieChart from "./PieChart";
import LineChartHero from "./LineChart";
import AreaChartHero from "./AreaChart";
import { PageRequestError } from "components/PageRequestError";

const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };

function getTotalTime(times) {
  const totalMoment = moment("00:00", "HH:mm");
  // Add each time to the totalMoment
  times.forEach((time) => {
    const [hours, minutes] = time.split(":");
    totalMoment.add(hours, "hours").add(minutes, "minutes");
  });
  // Format the total time as a string
  return totalMoment.format("HH:mm");
}

function getDuration(start, end) {
  const startTime = moment(start, "HH:mm");
  const endTime = moment(end, "HH:mm");
  const duration = moment.duration(endTime.diff(startTime));
  const hours = duration.hours();
  const minutes = duration.minutes();
  return `${hours}:${minutes}`;
}

function isValidJSON(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (error) {
    return false;
  }
}

const DashboardLoader = () => (
  <div className="flex flex-column" style={{ gap: "0.75rem" }}>
    <Skeleton width="100%" height="250px"></Skeleton>
    <div className="flex flex-row" style={{ gap: "0.75rem" }}>
      <Skeleton width="33.33%" height="300px"></Skeleton>
      <Skeleton width="33.33%" height="300px"></Skeleton>
      <Skeleton width="33.33%" height="300px"></Skeleton>
    </div>
  </div>
);

const convertToNormalDateTime = (utcDateTimeString) =>
  moment(utcDateTimeString).seconds(0).format("YYYY-MM-DD HH:mm:ss");

export default function Dashboard() {
  const {
    GET_ACTIONS_BY_MODULE,
    RUN_METRICS_QUERY,
    GET_METRICS_BY_MODULE,
    GET_METRICS,
    EDIT_MODULE,
    GET_ORG_URL,
  } = URLS;
  const {
    METRIC_CARD,
    METRIC_TABLE,
    METRIC_CARD_LITE,
    BAR_CHART,
    JOURNEY_CHART_REALTIME,
    FILTERS,
    PIE_DONUT_CHART,
    LINE_CHART,
    AREA_CHART,
  } = METRIC_TYPES;
  const toast = useRef(null);
  const mainScreenRef = useRef(null);
  const divRef = useRef(null);
  const { moduleId = "" } = useParams();
  const location = useLocation();
  const api = useApi();
  const auth = useAuth();
  const app = useApp();
  const { code: selectedOrgCode } = app.selectedOrg || {};
  const [downloadModuleFile, isDownloadModuleLoading] = exportModule(moduleId);
  const [actions, setActions] = useState([]);
  const [moduleData, setModuleData] = useState({});
  const [groupByMetricType, setGroupByMetricType] = useState({});
  const [layoutProps, setLayoutProps] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isPageRefreshed, setIsPageRefreshed] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [metricFilters, setMetricFilters] = useState([]);
  const [isJourneyTableFullScreen, setIsJourneyTableFullScreen] =
    useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [handleFilters, setHandleFilters] = useState({});
  const [onChangeLayout, setOnChangeLayout] = useState([]);
  const [isLayoutSaveLoading, setIsLayoutSaveLoading] = useState(false);
  const [isLoadingForPDFExport, setIsLoadingForPDFExport] = useState(false);
  const [showLive, setShowLive] = useState("");
  const appliedFiltersRef = useRef(appliedFilters);
  const moduleConfig = isValidJSON(moduleData.module_config)
    ? JSON.parse(moduleData.module_config)
    : {};
  const { t } = useTranslation();
  const NEXT_AUTO_REFRESH_IN = t("NEXT_AUTO_REFRESH_IN");
  const PAGE_IS_REFRESHING = t("PAGE_IS_REFRESHING");
  const FULL_SCREEN = t("FULL_SCREEN");
  const LAYOUT_SAVED_SUCCESSFULLY = t("LAYOUT_SAVED_SUCCESSFULLY");
  const FAILED = t("FAILED");
  const ACCESS_DENIED = t("ACCESS_DENIED");
  const NO_DATA_FOUND = t("NO_DATA_FOUND");
  const FAILED_TO_SAVE_LAYOUT = t("FAILED_TO_SAVE_LAYOUT");
  const EXPORT_MODULE = t("EXPORT_MODULE");
  const SUCCESS = t("SUCCESS");
  const MODULE_FILE_DOWNLOADED_SUCCESSFULLY = t(
    "MODULE_FILE_DOWNLOADED_SUCCESSFULLY"
  );
  const ERROR = t("ERROR");
  const FAILED_TO_DOWNLOAD_THE_FILE = t("FAILED_TO_DOWNLOAD_THE_FILE");
  const EXPORT_TO_PDF = t("EXPORT_TO_PDF");
  const EXPORT_TO_IMAGE = t("EXPORT_TO_IMAGE");
  const SAVE_LAYOUT = t("SAVE_LAYOUT");
  const REFRESH_DASHBOARD = t("REFRESH_DASHBOARD");
  const FILTER = t("FILTER");
  const IMAGE_FILE_DOWNLOADED_SUCCESSFULLY = t(
    "IMAGE_FILE_DOWNLOADED_SUCCESSFULLY"
  );
  const FAILED_TO_DOWNLOAD_IMAGE_FILE = t("FAILED_TO_DOWNLOAD_IMAGE_FILE");
  const PDF_FILE_DOWNLOADED_SUCCESSFULLY = t(
    "PDF_FILE_DOWNLOADED_SUCCESSFULLY"
  );
  const FAILED_TO_DOWNLOAD_PDF_FILE = t("FAILED_TO_DOWNLOAD_PDF_FILE");
  const APPLIED_FILTERS = t("APPLIED_FILTERS");

  const { module_name = "" } = moduleData;
  const { filters = [] } = moduleConfig;
  const searchParams = new URLSearchParams(location.search);
  const legendValue = Number(searchParams.get("legend"));
  const [countdown, setCountdown] = useState(0);
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    // Set the document title
    document.title = `${module_name} - ${orgName}`;

    // Optional: Clean up if needed
    return () => {
      // Reset the title or do some clean up if needed
      document.title = "Hospital metrics";
    };
  }, [module_name, orgName]); // The effect runs when `title` changes

  useEffect(() => {
    Pusher.logToConsole = true; // Optional: for debugging
    const client = new Pusher("4a909f116e4924d7682d", {
      cluster: "ap1",
      forceTLS: false,
      disableStats: true,
      encrypted: true,
      enabledTransports: ["ws", "wss"],
    });
    const connectionStateChangeHandler = () => {
      setShowLive(client.connection.state);
    };
    client.connection.bind("state_change", connectionStateChangeHandler);
    const channel = client.subscribe("patient-journey");
    channel.bind("data-posted", (data) => {
      fetchData(true);
    });
    // Cleanup on component unmount
    return () => {
      client.unsubscribe("patient-journey");
    };
  }, []);

  useEffect(() => {
    let intervalId;
    if (moduleId && Object.keys(moduleData).length) {
      clearInterval(intervalId);
      fetchData();
      if (moduleConfig.refreshIntervalInSec) {
        intervalId = setInterval(() => {
          setCountdown((prevCountdown) => {
            if (prevCountdown === 0) {
              // Execute your code here when the timer reaches 0
              fetchData(true);
              return moduleConfig.refreshIntervalInSec; // Reset seconds
            }
            return prevCountdown - 1;
          });
        }, 1000);
      }
    }
    return () => clearInterval(intervalId);
  }, [moduleId, JSON.stringify(moduleData)]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    if (moduleId) {
      setAppliedFilters({});
      setHandleFilters({});
    }
  }, [moduleId]);

  useEffect(() => {
    if (moduleConfig.refreshIntervalInSec && countdown >= 0) {
      setCountdown(moduleConfig.refreshIntervalInSec);
    }
  }, [moduleConfig.refreshIntervalInSec, moduleId]);

  useEffect(() => {
    if (moduleData.org_code) {
      api.get(`${GET_ORG_URL}/code/${moduleData.org_code}`).then((response) => {
        const [firstArray] = get(response, "data.data", []);
        const { name: org } = firstArray || {};
        setOrgName(org);
      });
    }
  }, [moduleData.org_code]);

  const fetchData = (disableLoading) => {
    getMetricsByModule(moduleId, disableLoading);
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(appliedFilters)]);

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const handleFullscreenChange = () => {
    if (
      document.fullscreenElement === mainScreenRef.current ||
      document.mozFullScreenElement === mainScreenRef.current ||
      document.webkitFullscreenElement === mainScreenRef.current ||
      document.msFullscreenElement === mainScreenRef.current
    ) {
      setIsFullScreen(true);
    } else {
      setIsJourneyTableFullScreen(false);
      setIsFullScreen(false);
    }
  };

  const updateModule = () => {
    setIsLayoutSaveLoading(true);
    const postData = {
      module_config: JSON.stringify({
        ...moduleConfig,
        layout: onChangeLayout,
      }),
      metrics: moduleData.metrics,
    };
    api
      .put(`${EDIT_MODULE}/${moduleId}`, postData)
      .then((res) => {
        setIsLayoutSaveLoading(false);
        toast.current.show({
          severity: SUCCESS,
          summary: SUCCESS,
          detail: LAYOUT_SAVED_SUCCESSFULLY,
          life: 5000,
        });
      })
      .catch((err) => {
        setIsLayoutSaveLoading(false);
        toast.current.show({
          severity: ERROR,
          summary: FAILED,
          detail: FAILED_TO_SAVE_LAYOUT,
          life: 5000,
        });
      });
  };

  const getDRDTooltip = (target, tooltip) => (
    <>
      <Tooltip target={`.${target}`} />
      <i
        style={{ width: "fit-content" }}
        className={`pi pi-bars drag-handle cursor-pointer ${target}`}
        data-pr-tooltip={tooltip}
      />
    </>
  );

  const fetchMetricsData = async (data, disableLoading) => {
    !disableLoading ? setIsLoading(true) : setIsPageRefreshed(true);
    const promises = (data.metrics || []).map((id) =>
      api.get(`${GET_METRICS}/${id}`).then((response) => {
        return response ? response.data : {};
      })
    );
    try {
      const results = await Promise.all(promises);
      await getQueryData(results, data.module_config, disableLoading);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      !disableLoading ? setIsLoading(false) : setIsPageRefreshed(false);
    }
  };

  const getMetricsByModule = async (moduleId, disableLoading) => {
    !disableLoading ? setIsLoading(true) : setIsPageRefreshed(true);
    try {
      const res = await api.get(`${GET_METRICS_BY_MODULE}/${moduleId}`);
      setModuleData(get(res, "data", {}));
      await getActionsByModule(moduleId, disableLoading);
      await fetchMetricsData(get(res, "data", []), disableLoading);
    } catch (error) {
      setModuleData({});
    } finally {
      !disableLoading ? setIsLoading(false) : setIsPageRefreshed(false);
    }
  };

  const getActionsByModule = async (moduleId, disableLoading) => {
    !disableLoading ? setIsLoading(true) : setIsPageRefreshed(true);
    try {
      const res = await api.get(`${GET_ACTIONS_BY_MODULE}/${moduleId}`);
      setActions(get(res, "data.data", []));
    } catch (error) {
      setActions([]);
    } finally {
      !disableLoading ? setIsLoading(false) : setIsPageRefreshed(false);
    }
  };

  const runMetricQuery = async (postData = {}) => {
    return await api.post(RUN_METRICS_QUERY, postData);
  };

  const getQueryData = async (data, module_config, disableLoading) => {
    !disableLoading ? setIsLoading(true) : setIsPageRefreshed(true);
    const promises = (data || []).map((metric) => {
      const filtersObj =
        metric.metric_type === FILTERS ? {} : appliedFiltersRef.current;
      let location = {};
      if (
        isValidJSON(metric.metric_config) &&
        JSON.parse(metric.metric_config) &&
        JSON.parse(metric.metric_config).location
      ) {
        location = { location: JSON.parse(metric.metric_config).location };
      }
      return runMetricQuery(
        metric.metric_type === METRIC_CARD_LITE
          ? {
              metric_id: metric.id,
              module_id: moduleId,
              ...appliedFiltersRef.current,
            }
          : {
              metric_id: metric.id,
              module_id: moduleId,
              ...filtersObj,
              ...location,
            }
      ).then((response) => {
        return { ...response.data.result, ...metric };
      });
    });
    try {
      const results = await Promise.all(promises);
      const groupedByType = groupBy(results, "metric_type");
      for (const key in groupedByType) {
        if (groupedByType.hasOwnProperty(key)) {
          if ([METRIC_CARD, METRIC_CARD_LITE].includes(key)) {
            groupedByType[METRIC_CARD] = [
              ...(groupedByType[METRIC_CARD] || []),
              ...(groupedByType[METRIC_CARD_LITE] || []),
            ];
            break;
          }
        }
      }
      const {
        METRIC_CARD_LITE: CARD_LITE,
        JOURNEY_CHART_REALTIME,
        FILTERS = [],
        ...newObj
      } = groupedByType;
      setMetricFilters(FILTERS);
      setGroupByMetricType({
        JOURNEY_CHART_REALTIME,
        ...newObj,
      });
      let generatedLayout = generateLayout({
        JOURNEY_CHART_REALTIME,
        ...newObj,
      });
      const config = isValidJSON(module_config)
        ? JSON.parse(module_config)
        : {};
      //for layout
      if (
        config.layout &&
        generatedLayout?.layouts?.lg.length === config.layout.length
      ) {
        const getObj = Object.keys(breakpoints).reduce((acc, curr) => {
          acc[curr] = config.layout;
          return acc;
        }, {});
        generatedLayout.layouts = getObj;
      }
      setLayoutProps(generatedLayout);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      !disableLoading ? setIsLoading(false) : setIsPageRefreshed(false);
    }
  };

  const mapMetricCardDataWithConfig = (groupBasedData) => {
    return (groupBasedData || []).reduce((acc, current) => {
      const metricConfig = current.metric_config;
      const [firstObj = {}] = current.data;
      if (current.metric_type === METRIC_CARD) {
        metricConfig.patients = firstObj.unique_mrn_count || 0;
        metricConfig.time = firstObj.average_duration || 0;
        metricConfig.type = current.metric_type || "-";
      } else {
        metricConfig.value = firstObj.value || "-";
      }
      acc.push(metricConfig);
      return acc;
    }, []);
  };

  const filterActionByName = (name) => {
    return actions
      .filter(
        (action) =>
          action.action_name === name && action.module_id === Number(moduleId)
      )
      .map((d) => d.id);
  };

  const getActionByName = (name) => {
    return (
      actions.find(
        (action) =>
          action.action_name === name && action.module_id === Number(moduleId)
      ) || {}
    );
  };

  const getActionById = (id) => {
    return actions.find((action) => action.id === id) || {};
  };

  const findIsLateBasedOnSLA = (sla_time, eventTime) => {
    let isSlaTimeLate = false;
    const slaTime = moment(sla_time, "HH:mm");
    const duration = moment(eventTime, "HH:mm");
    if (slaTime.isBefore(duration)) {
      isSlaTimeLate = true;
    }
    if (slaTime.isSame(duration)) {
      isSlaTimeLate = false;
    }
    return isSlaTimeLate;
  };

  const mapMetricTableDataWithConfig = (groupBasedData) => {
    return (groupBasedData || []).reduce((acc, current) => {
      const metricConfig = current.metric_config;
      const modifiedData = (metricConfig.values || []).map((val) => {
        const actionIds = filterActionByName(val.key);
        const { average_duration, unique_mrn_count } =
          (current.data || []).find((d) => actionIds.includes(d.action_id)) ||
          {};
        return {
          ...val,
          time: average_duration,
          count: unique_mrn_count,
        };
      });
      acc.push({
        ...metricConfig,
        values: modifiedData,
      });
      return acc;
    }, []);
  };

  const mapBarChartDatawithConfig = (groupBasedData) => {
    const metric_config = groupBasedData.metric_config || {};
    metric_config.labels = (groupBasedData.data || []).map((d) => d.hour);
    const afterData =
      (metric_config.datasets || []).find((d) => d.label === "After") || {};
    const beforeData =
      (metric_config.datasets || []).find((d) => d.label === "Before") || {};
    const data = (groupBasedData.data || []).map((d) => d.unique_mrn_count);
    metric_config.datasets = [
      { ...afterData, data },
      { ...beforeData, data },
    ];
    return { metric_config, excelData: groupBasedData.data };
  };

  const mapPieChartWithConfig = (groupBasedData) => {
    const metric_config = groupBasedData[0].metric_config || {};
    return {
      data: (groupBasedData[0].data || []).map((val) => {
        const { action_name } = getActionById(val[metric_config.name]);
        return {
          name: action_name,
          value: val[metric_config.value],
        };
      }),
      title: metric_config.title,
      chartType: metric_config.chartType,
      excelData: groupBasedData[0].data,
    };
  };

  const mapLineChartWithConfig = (groupBasedData) => {
    const metric_config = groupBasedData[0].metric_config || {};
    const uniqueActions = [
      ...new Set(
        (groupBasedData[0].data || [])
          .reduce((acc, val) => {
            return [...acc, ...(val[metric_config.comparisonvalue] || [])];
          }, [])
          .filter((d) => d)
      ),
    ].map((d) => {
      const { action_name } = getActionById(d);
      return action_name;
    });
    return {
      data: (groupBasedData[0].data || []).map((val) => {
        const data = uniqueActions.reduce((acc, value) => {
          acc[value] = val[metric_config.yvalue] || 0;
          return acc;
        }, {});
        return {
          date: val[metric_config.xvalue],
          ...data,
        };
      }),
      categories: uniqueActions,
      title: metric_config.title,
      chartType: metric_config.chartType,
      excelData: groupBasedData[0].data,
    };
  };

  const mapJourneyDataWithConfig = (groupBasedData) => {
    const metric_config = groupBasedData[0].metric_config || {};
    const patientsData =
      groupBasedData.length > 0 ? groupBasedData[0].data : [];
    const rowData = (patientsData || []).map((patient) => {
      const steps = (metric_config.steps || []).map((step, index) => {
        const { id: actionId, sla_time } = getActionByName(step.key);
        const foundIndex = (patient.action_ids || []).findIndex(
          (d) => d === actionId
        );
        let time = "";
        let action_id = "";
        let action_sla_time = "";
        let date = "";
        let eventTime = "";
        if (foundIndex > -1) {
          time = moment(patient.event_time[foundIndex]).format("HH:mm");
          action_id = actionId;
          action_sla_time = sla_time;
          date = moment(patient.event_time[foundIndex]).format("DD MMM YYYY");
          eventTime = patient.event_time[foundIndex];
        }
        return {
          date,
          time,
          id: index,
          actionId: action_id,
          action_sla_time,
          label: step.label,
          eventTime: eventTime,
          actionFlowId: actionId,
        };
      });
      const action_ids = (steps || []).map((d) => d.actionId).filter((d) => d);
      const lastValue = action_ids[action_ids.length - 1];
      const { action_name } = getActionById(lastValue);
      const lastStep = (metric_config.steps || [])
        .map((step) => step.key)
        .indexOf(action_name);
      let details = "";
      if (patient.personnel_details && patient.personnel_details.length) {
        details = patient.personnel_details[0];
      }
      let gender = "";
      if (patient.personnel_gender && patient.personnel_gender.length) {
        gender = patient.personnel_gender[0];
      }
      return {
        ...patient,
        name: patient.personnel_name,
        details,
        radiologist: patient.dr_name,
        imageSource:
          "https://images.pexels.com/photos/39866/entrepreneur-startup-start-up-man-39866.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
        steps,
        gender,
        number: patient.mrn,
        encounterNumber: patient.encounter_number,
        lastStep: lastStep + 1,
        metric_config,
      };
    });
    const filteredData = rowData
      .filter((d) => d.lastStep > -1)
      .filter((d) => !(d.steps || []).every((s) => !s.time));

    const slaTimeData = filteredData.map((d) => {
      const steps = (d.steps || []).map((step, i) => {
        let a = {};
        if (i) {
          const duration = getDuration(d.steps[i - 1].time, step.time);
          const isSlaTimeLate = findIsLateBasedOnSLA(
            step.action_sla_time,
            duration
          );
          a = { isSlaTimeLate, duration };
        } else {
          a = { isSlaTimeLate: false, duration: "" };
        }
        return { ...step, ...a };
      });
      return {
        ...d,
        steps,
        timeString: getTotalTime(steps.map((d) => d.duration)),
        isLate: steps.some((d) => d.isSlaTimeLate),
      };
    });
    let d = slaTimeData;
    if (!Object.keys(appliedFiltersRef.current).length) {
      d = slaTimeData.filter((d) => {
        const lastValue = d.steps[d.steps.length - 1];
        return !lastValue.time;
      });
    }

    let sorted = d;
    if (
      !Object.keys(appliedFiltersRef.current).length ||
      appliedFiltersRef.current?.order_by === "event_time"
    ) {
      sorted = [...d].sort((a, b) => {
        const aIndex = a.steps.findIndex((value) => value.eventTime !== "");
        const bIndex = b.steps.findIndex((value) => value.eventTime !== "");
        return appliedFiltersRef.current?.order_type === "ASC"
          ? moment(a.steps[aIndex].eventTime) -
              moment(b.steps[bIndex].eventTime)
          : moment(b.steps[bIndex].eventTime) -
              moment(a.steps[aIndex].eventTime);
      });
    }

    if (appliedFiltersRef.current?.order_by === "elapsed_time") {
      sorted = [...d].sort((a, b) => {
        return appliedFiltersRef.current?.order_type === "ASC"
          ? moment(a.timeString, "HH:mm") - moment(b.timeString, "HH:mm")
          : moment(b.timeString, "HH:mm") - moment(a.timeString, "HH:mm");
      });
    }
    return {
      rowData: sorted,
      tableHeadData: metric_config.tableHeadData || [],
      excelData: patientsData,
    };
  };

  const groupByIdFromConfig = (metricData) => {
    const modifiedData = (metricData || []).map((data) => ({
      ...data,
      metric_config: isValidJSON(data.metric_config)
        ? JSON.parse(data.metric_config)
        : {},
    }));
    return groupBy(modifiedData, (obj) => obj.metric_config.groupId);
  };

  const renderUIbasedOnMetricType = (metricType, metricData) => {
    const groupedById = groupByIdFromConfig(metricData);
    switch (metricType) {
      case METRIC_CARD_LITE:
      case METRIC_CARD: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className={`card flex flex-column`}
              style={{
                padding: "1rem",
                borderColor: `#${moduleData.color}`,
              }}
            >
              <ScansBoard
                cardsData={mapMetricCardDataWithConfig(groupedById[key])}
                excelData={groupedById[key]}
                getDRDTooltip={getDRDTooltip}
              />
            </div>
          ))
        );
      }
      case METRIC_TABLE: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className="card flex flex-column"
              style={{
                borderColor: `#${moduleData.color}`,
              }}
            >
              <ScansTable
                rows={mapMetricTableDataWithConfig(groupedById[key])}
                excelData={groupedById[key]}
                getDRDTooltip={getDRDTooltip}
              />
            </div>
          ))
        );
      }
      case BAR_CHART: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className="card flex flex-column scans-bar-chart"
              style={{
                borderColor: `#${moduleData.color}`,
              }}
            >
              {" "}
              <ScansBarChart
                {...mapBarChartDatawithConfig(
                  groupedById[key].length > 0 ? groupedById[key][0] : {}
                )}
                getDRDTooltip={getDRDTooltip}
              />
            </div>
          ))
        );
      }
      case PIE_DONUT_CHART: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className="card flex flex-column"
              style={{
                borderColor: `#${moduleData.color}`,
              }}
            >
              <PieChart
                chartData={mapPieChartWithConfig(groupedById[key])}
                getDRDTooltip={getDRDTooltip}
              />
            </div>
          ))
        );
      }
      case LINE_CHART: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className="card flex flex-column"
              style={{
                borderColor: `#${moduleData.color}`,
              }}
            >
              <LineChartHero
                chartData={mapLineChartWithConfig(groupedById[key])}
                getDRDTooltip={getDRDTooltip}
              />
            </div>
          ))
        );
      }
      case AREA_CHART: {
        return (
          !isJourneyTableFullScreen &&
          Object.keys(groupedById).map((key, index) => (
            <div
              key={`${metricType}_${index + 1}`}
              className="card flex flex-column"
              style={{
                borderColor: `#${moduleData.color}`,
              }}
              id={`${metricType}_${index + 1}`}
            >
              <AreaChartHero
                chartData={mapLineChartWithConfig(groupedById[key])}
                getDRDTooltip={getDRDTooltip}
                divId={`${metricType}_${index + 1}`}
              />
            </div>
          ))
        );
      }
      case JOURNEY_CHART_REALTIME: {
        return Object.keys(groupedById).map((key, index) => {
          const formattedData = mapJourneyDataWithConfig(groupedById[key]);
          return (formattedData.rowData || []).length ? (
            <div
              key={`${metricType}_${index + 1}`}
              className={`card flex flex-column fullscreen-container ${
                isJourneyTableFullScreen ? "journeytable-fullscreen" : ""
              }`}
              style={{
                borderColor: `#${moduleData.color}`,
                borderTopWidth: "4px",
              }}
              id={`${metricType}_${index + 1}`}
            >
              <Stages
                data={formattedData}
                toggleFullScreen={toggleFullScreen}
                isJourneyTableFullScreen={isJourneyTableFullScreen}
                isFullScreen={isFullScreen}
                getDRDTooltip={getDRDTooltip}
                legendValue={legendValue}
                divId={`${metricType}_${index + 1}`}
                showLive={showLive}
                isPatientFilterApplied={
                  appliedFiltersRef?.current?.personnel_name
                }
                moduleData={moduleData}
              />
            </div>
          ) : null;
        });
      }
      default:
        return <></>;
    }
  };

  const generateLayout = (metricData) => {
    const layouts = {};
    const rowHeight = 150;
    Object.keys(breakpoints).forEach((breakpoint) => {
      // const cols = Math.ceil(divCount / (breakpoints[breakpoint] / 300)); // Adjust divs per row based on breakpoint width
      const cols = 3;
      let layout = [];
      let mainIndex = 0;
      let index = 0;
      for (const key in metricData) {
        const groupedById = groupByIdFromConfig(metricData[key]);
        if (!mainIndex) {
          index = 0;
        }
        let keyIndex = 0;
        for (const k in groupedById) {
          layout.push(
            key === JOURNEY_CHART_REALTIME
              ? {
                  i: `${key}_${keyIndex + 1}`,
                  x: index % cols,
                  y: Math.floor(index / cols),
                  w: 3,
                  h: groupedById[k][0].data.length > 1 ? 8 * 0.6 : 1.2 + 0.8,
                }
              : {
                  i: `${key}_${keyIndex + 1}`,
                  x: index % cols,
                  y: Math.floor(index / cols),
                  w: 1,
                  h: 2,
                }
          );
          keyIndex++;
          index++;
        }
        mainIndex++;
      }
      layouts[breakpoint] = layout;
    });
    return { layouts, breakpoints, rowHeight };
  };

  const toggleFullScreen = (fullscreenFor = "mainscreen") => {
    const fullscreenRef = mainScreenRef;
    if (!document.fullscreenElement) {
      if (fullscreenRef.current.requestFullscreen) {
        fullscreenRef.current.requestFullscreen();
      } else if (fullscreenRef.current.mozRequestFullScreen) {
        // Firefox
        fullscreenRef.current.mozRequestFullScreen();
      } else if (fullscreenRef.current.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        fullscreenRef.current.webkitRequestFullscreen();
      } else if (fullscreenRef.current.msRequestFullscreen) {
        // IE/Edge
        fullscreenRef.current.msRequestFullscreen();
      }
      setIsFullScreen(true);
      fullscreenFor === "table" && setIsJourneyTableFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullScreen(false);
        console.log("isJourneyTableFullScreen", isJourneyTableFullScreen);
        isJourneyTableFullScreen && setIsJourneyTableFullScreen(false);
      }
    }
  };

  const getInputTypeUI = (data) => {
    switch (data.inputType) {
      case "dropdown": {
        return (
          <div className="flex-auto">
            <label htmlFor="dropdown-12h" className="font-bold block mb-2">
              {data.label}
            </label>
            <Dropdown
              id="dropdown-12h"
              value={handleFilters[data.key] || null}
              onChange={(e) =>
                setHandleFilters({
                  ...handleFilters,
                  [data.key]: e.value || "",
                })
              }
              options={
                data.isDataFromApi
                  ? (data.apiData || [])
                      .filter((d) => d[data.key])
                      .map((d) => ({
                        name: d[data.key],
                        code: d[data.key],
                      }))
                  : data.dropdownData || []
              }
              optionLabel="name"
              placeholder={data.label}
              filter
              className="w-full"
              appendTo="self"
              showClear
            />
          </div>
        );
      }
      case "daterange": {
        return (
          <div className="flex-auto">
            <label htmlFor="calendar-12h" className="font-bold block mb-2">
              {data.label}
            </label>
            <div className="flex gap-3">
              <Calendar
                id="calendar-24h"
                value={
                  handleFilters[data.key] ? handleFilters[data.key].code : null
                }
                onChange={(e) =>
                  setHandleFilters({
                    ...handleFilters,
                    [data.key]: { name: data.key, code: e.value },
                  })
                }
                selectionMode="range"
                readOnlyInput
                hideOnRangeSelection
                className="w-full"
                showIcon
                hideOnDateTimeSelect
              />
              <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={(e) =>
                  setHandleFilters({
                    ...handleFilters,
                    [data.key]: null,
                  })
                }
              />
            </div>
          </div>
        );
      }
      case "datetime": {
        return (
          <div className="flex-auto">
            <label htmlFor="calendar-12h" className="font-bold block mb-2">
              {data.label}
            </label>
            <div className="flex gap-3">
              <Calendar
                id="datetime24h"
                value={
                  handleFilters[data.key]
                    ? new Date(handleFilters[data.key].code)
                    : null
                }
                onChange={(e) => {
                  setHandleFilters({
                    ...handleFilters,
                    [data.key]: {
                      name: data.key,
                      code: convertToNormalDateTime(e.value),
                    },
                  });
                }}
                readOnlyInput
                showIcon
                showTime
                hourFormat="24"
                className="w-full"
                hideOnDateTimeSelect
              />
              <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={(e) =>
                  setHandleFilters({
                    ...handleFilters,
                    [data.key]: null,
                  })
                }
              />
            </div>
          </div>
        );
      }
      default:
        return <></>;
    }
  };

  const getFilters = () =>
    filters.map((data) => {
      const { data: apiData = [] } =
        (metricFilters || []).find((d) => d.name === data.key) || {};
      return getInputTypeUI({
        ...data,
        apiData,
      });
    });

  const convertFiltersData = () => {
    const filteredData = {};
    Object.entries(handleFilters).forEach(([key, value]) => {
      // Apply your filtering condition here
      if (value) {
        const val = value.code;
        if (typeof val === "string") {
          filteredData[key] = val;
        } else if (typeof val === "object") {
          const [first, second] = val;
          filteredData["start_date"] = moment(first).format("YYYY-MM-DD");
          filteredData["end_date"] = moment(second).format("YYYY-MM-DD");
        }
      }
    });
    return filteredData;
  };

  const getFiltersPanel = () => (
    <Sidebar
      visible={openFilter}
      position="right"
      onHide={() => {
        setOpenFilter(false);
      }}
      dismissable={false}
    >
      <h5>{t("APPLY_FILTERS")}</h5>
      <hr />
      <div className="flex flex-column" style={{ gap: "10px" }}>
        {getFilters()}
        <div className="flex justify-content-center" style={{ gap: "10px" }}>
          <Button
            label={t("APPLY")}
            onClick={() => {
              const filteredData = convertFiltersData();
              setAppliedFilters(filteredData);
              setOpenFilter(false);
            }}
          />
          <Button
            label={t("CLEAR")}
            onClick={() => {
              setAppliedFilters({});
              setHandleFilters({});
              setOpenFilter(false);
            }}
          />
        </div>
      </div>
    </Sidebar>
  );

  const getActions = () => {
    const superAdminAccess =
      auth.user.roleName === "Super Admin"
        ? [
            {
              label: EXPORT_MODULE,
              icon: "pi pi-download",
              command: () =>
                downloadModuleFile((res) => {
                  res
                    ? toast.current.show({
                        severity: SUCCESS,
                        summary: SUCCESS,
                        detail: MODULE_FILE_DOWNLOADED_SUCCESSFULLY,
                        life: 5000,
                      })
                    : toast.current.show({
                        severity: ERROR,
                        summary: ERROR,
                        detail: FAILED_TO_DOWNLOAD_THE_FILE,
                        life: 5000,
                      });
                }),
            },
            {
              label: EXPORT_TO_PDF,
              icon: "pi pi-file-pdf",
              command: () => downloadPDF(),
            },
            {
              label: EXPORT_TO_IMAGE,
              icon: "pi pi-image",
              command: () => downloadImage(),
            },
            {
              label: SAVE_LAYOUT,
              icon: "pi pi-save",
              command: () => updateModule(),
            },
          ]
        : [];
    const fullScreenAccess = moduleConfig.fullScreen
      ? [
          {
            label: FULL_SCREEN,
            icon: isFullScreen
              ? "pi pi-window-minimize"
              : "pi pi-window-maximize",
            command: () => toggleFullScreen(),
          },
        ]
      : [];
    const isManualRefreshAccess = moduleConfig.isManualRefresh
      ? [
          {
            label: REFRESH_DASHBOARD,
            icon: "pi pi-refresh",
            command: () => fetchData(),
          },
        ]
      : [];
    return [
      ...superAdminAccess,
      {
        label: FILTER,
        icon: "pi pi-filter",
        command: () => {
          setOpenFilter(true);
        },
      },
      ...isManualRefreshAccess,
      ...fullScreenAccess,
    ];
  };

  const downloadImage = () => {
    const container = divRef.current;
    setIsLoadingForPDFExport(true);
    html2canvas(container)
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.download = `${moduleData.module_name} ${moment().format(
          "YYYY-MM-DD HH-mm-ss"
        )}.png`;
        link.href = imgData;
        link.click();
        setIsLoadingForPDFExport(false);
        toast.current.show({
          severity: SUCCESS,
          summary: SUCCESS,
          detail: IMAGE_FILE_DOWNLOADED_SUCCESSFULLY,
          life: 5000,
        });
      })
      .catch((err) => {
        toast.current.show({
          severity: ERROR,
          summary: ERROR,
          detail: FAILED_TO_DOWNLOAD_IMAGE_FILE,
          life: 5000,
        });
        setIsLoadingForPDFExport(false);
      });
  };

  const downloadPDF = () => {
    const input = divRef.current;
    setIsLoadingForPDFExport(true);
    html2canvas(input, { scrollY: -window.scrollY })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(
          `${moduleData.module_name} ${moment().format(
            "YYYY-MM-DD HH-mm-ss"
          )}.pdf`
        );
        setIsLoadingForPDFExport(false);
        toast.current.show({
          severity: SUCCESS,
          summary: SUCCESS,
          detail: PDF_FILE_DOWNLOADED_SUCCESSFULLY,
          life: 5000,
        });
      })
      .catch((err) => {
        toast.current.show({
          severity: ERROR,
          summary: ERROR,
          detail: FAILED_TO_DOWNLOAD_PDF_FILE,
          life: 5000,
        });
        setIsLoadingForPDFExport(false);
      });
  };

  const memoizedDashboard = useMemo(
    () => (
      <>
        {(layoutProps?.layouts?.lg || []).length ? (
          <CustomiseLayout
            className="layout"
            {...layoutProps}
            cols={{ lg: 3, md: 3, sm: 3, xs: 2, xxs: 2 }}
            draggableHandle=".drag-handle"
            onLayoutChange={(e) => setOnChangeLayout(e)}
          >
            {Object.entries(groupByMetricType).map(([key, value]) =>
              renderUIbasedOnMetricType(key, value)
            )}
          </CustomiseLayout>
        ) : null}
        {!Object.entries(groupByMetricType).length && !isLoading && (
          <h1>{NO_DATA_FOUND}</h1>
        )}
      </>
    ),
    [
      JSON.stringify(groupByMetricType),
      isLoading,
      JSON.stringify(layoutProps),
      isJourneyTableFullScreen,
      isFullScreen,
    ]
  );

  const convertSecondsToTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return (
      <span className="text-2xl font-bold text-gray-700">
        {String(hours).padStart(2, "0")} : {String(minutes).padStart(2, "0")} :{" "}
        {String(seconds).padStart(2, "0")}
      </span>
    );
  };

  function dashboardTopHeader() {
    return (
      <div className="flex align-items-center justify-content-between w-full">
        <DashboardTitle
          title={moduleData.module_name}
          imageSrc={moduleData.icon}
          bgColor={`#${moduleData.color}`}
          color="white"
          subTitle={orgName}
        />
        <div className="flex gap-5 align-items-center">
          {moduleConfig.refreshIntervalInSec ? (
            <div className="flex align-items-center" style={{ gap: "8px" }}>
              {isPageRefreshed ? (
                <>
                  <span
                    className="ml-2 text-xx text-gray-500 relative"
                    style={{ right: "62px" }}
                  >
                    {PAGE_IS_REFRESHING}
                  </span>
                  <div
                    className="flex justify-content-center align-items-center relative"
                    style={{ right: "62px" }}
                  >
                    <i className="pi pi-spin pi-spinner text-2xl cursor-wait" />
                  </div>
                </>
              ) : (
                <>
                  <span
                    className="ml-2 text-xx text-gray-500 relative"
                    style={{ right: "62px" }}
                  >
                    {NEXT_AUTO_REFRESH_IN}
                  </span>
                  <div
                    className="flex justify-content-center align-items-center relative"
                    style={{ right: "62px" }}
                  >
                    {convertSecondsToTime(countdown)}
                  </div>
                </>
              )}
            </div>
          ) : null}
          {isDownloadModuleLoading ||
          isLayoutSaveLoading ||
          isLoadingForPDFExport ? (
            <i className="pi pi-spin pi-spinner text-2xl cursor-wait" />
          ) : !isJourneyTableFullScreen ? (
            <>
              <Tooltip
                target=".speeddial-top-right .p-speeddial-action"
                position="left"
              />
              <SpeedDial
                model={getActions()}
                direction="left"
                className="speeddial-top-right"
                buttonClassName="p-button-primary"
                showIcon="pi pi-bars"
                hideIcon="pi pi-times"
                style={{ right: 0, position: "absolute" }}
              />
            </>
          ) : null}
          {getFiltersPanel()}
        </div>
      </div>
    );
  }

  const getFilterLabelByKey = (key) => {
    const { label = "" } = filters.find((filter) => filter.key === key) || {};
    return label;
  };

  const getChipLabel = (label, value) => (
    <div className="flex align-items-center" style={{ gap: "8px" }}>
      <span className="text-xx text-gray-500">{label}#: </span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );

  return (
    <main id="Dashboard" className="main-page">
      <Toast ref={toast} />
      {selectedOrgCode === moduleData.org_code || !selectedOrgCode ? (
        <section
          ref={mainScreenRef}
          className={`page-section q-pa-md fullscreen-container ${
            isFullScreen ? "fullscreen p-2" : ""
          }`}
        >
          <div className="flex mb-4">
            {isLoading ? (
              <Skeleton width="16rem" height="3rem"></Skeleton>
            ) : (
              dashboardTopHeader()
            )}
          </div>
          {!isLoading && Object.keys(appliedFilters).length ? (
            <div
              className="flex align-items-center flex-wrap"
              style={{ gap: "12px" }}
            >
              <div className="text-md font-bold flex">{APPLIED_FILTERS}# :</div>
              {Object.keys(appliedFilters).map((filter, index) => (
                <Chip
                  label={getChipLabel(
                    getFilterLabelByKey(filter),
                    appliedFilters[filter]
                  )}
                  key={index}
                  removable
                  onRemove={() => {
                    const clonedAppliedFilters = JSON.parse(
                      JSON.stringify(appliedFilters)
                    );
                    const clonedHandleFilters = JSON.parse(
                      JSON.stringify(handleFilters)
                    );
                    delete clonedAppliedFilters[filter];
                    delete clonedHandleFilters[filter];
                    setAppliedFilters(clonedAppliedFilters);
                    setHandleFilters(clonedHandleFilters);
                  }}
                />
              ))}
            </div>
          ) : null}
          <div className="container-fluid" ref={divRef}>
            {isLoading ? <DashboardLoader /> : <>{memoizedDashboard}</>}
          </div>
        </section>
      ) : selectedOrgCode &&
        moduleData.org_code &&
        selectedOrgCode !== moduleData.org_code ? (
        <PageRequestError
          error={{ response: { status: 403, message: ACCESS_DENIED } }}
        />
      ) : (
        ""
      )}
    </main>
  );
}
